import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { store } from './store/store';
import { bookingsAPI } from './utils/api';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BookingDashboard from './pages/BookingDashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Settings from './pages/Settings';
import Bookings from './pages/Bookings';
import NewBooking from './pages/NewBooking';
import BookingDetails from './pages/BookingDetails';
import LeafletDelivery from './pages/LeafletDelivery';
import Reports from './pages/Reports';
import UserProfile from './pages/UserProfile';
import SecuritySettings from './pages/SecuritySettings';
import Artworks from './pages/Artworks';
import Artwork from './pages/Artwork';
import ArtworkSchedule from './pages/ArtworkSchedule';
import DatabaseBackup from './pages/DatabaseBackup';
import FlatPlan from './pages/FlatPlan';
import FlatPlanEditor from './pages/FlatPlanEditor';
import AdSizeSettings from './pages/AdSizeSettings';
import Publications from './pages/Publications';
import PublicationView from './pages/PublicationView';
import PublicPublicationView from './pages/PublicPublicationView';
import ProtectedRoute from './components/ProtectedRoute';
import AuthProvider from './components/AuthProvider';
import './index.css';

// Role-aware redirect: Artwork-Only users land on /artwork, everyone else on /dashboard.
const RoleAwareHomeRedirect = () => {
  const { user } = useSelector(state => state.auth);
  const target = user?.role === 'view-only' ? '/artwork' : '/dashboard';
  return <Navigate to={target} replace />;
};

// Bounces stale /bookings/:id (and /bookings/:id/artwork-schedule) URLs to the
// new per-customer URLs. Backend resolves the customer for both flat IDs and
// pre-flatten legacy IDs via legacyBookingId.
const StaleBookingIdRedirect = ({ suffix = '' }) => {
  const params = useParams();
  const id = params.id || params.bookingId;
  const [target, setTarget] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    bookingsAPI
      .getRedirect(id)
      .then(res => {
        if (cancelled) return;
        if (res.data?.customerId) {
          setTarget(`/bookings/customer/${res.data.customerId}${suffix}`);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, suffix]);

  if (notFound) return <Navigate to="/bookings" replace />;
  if (target) return <Navigate to={target} replace />;
  return null;
};

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/view/publication/:id" element={<PublicPublicationView />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <BookingDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <Customers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <CustomerDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/new"
                element={
                  <ProtectedRoute>
                    <NewBooking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/customer/:customerId"
                element={
                  <ProtectedRoute>
                    <NewBooking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/customer/:customerId/view"
                element={
                  <ProtectedRoute>
                    <BookingDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/customer/:customerId/artwork-schedule"
                element={
                  <ProtectedRoute>
                    <ArtworkSchedule />
                  </ProtectedRoute>
                }
              />
              {/* Legacy URL bouncers — preserved so old bookmarks and links keep working */}
              <Route
                path="/bookings/edit/:id"
                element={
                  <ProtectedRoute>
                    <StaleBookingIdRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/:id"
                element={
                  <ProtectedRoute>
                    <StaleBookingIdRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/:bookingId/artwork-schedule"
                element={
                  <ProtectedRoute>
                    <StaleBookingIdRedirect suffix="/artwork-schedule" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/artworks"
                element={
                  <ProtectedRoute>
                    <Artworks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/artwork"
                element={
                  <ProtectedRoute>
                    <Artwork />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaflet-delivery"
                element={
                  <ProtectedRoute>
                    <LeafletDelivery />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/security"
                element={
                  <ProtectedRoute>
                    <SecuritySettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/database-backup"
                element={
                  <ProtectedRoute>
                    <DatabaseBackup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flat-plans"
                element={
                  <ProtectedRoute>
                    <FlatPlan />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flat-plan/:id"
                element={
                  <ProtectedRoute>
                    <FlatPlanEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ad-size-settings"
                element={
                  <ProtectedRoute>
                    <AdSizeSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/publications"
                element={
                  <ProtectedRoute>
                    <Publications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/publications/:id"
                element={
                  <ProtectedRoute>
                    <PublicationView />
                  </ProtectedRoute>
                }
              />
              <Route path="/app" element={<RoleAwareHomeRedirect />} />
            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop={true}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
              limit={3}
              toastStyle={{
                fontSize: '14px',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </Provider>
  );
}

export default App;
